import { useEffect, useState } from "react";
import { useTenantAuth } from "../../../../context/TeanantAuthProvider";
import "./SubSchoolSelector.css";
import { SearchableList } from "./Search/SearchableList";
import { useGetLightStudents } from "../../../../hooks/school/notification/useGetStudentList";
import { useGetLightTeachers } from "../../../../hooks/school/notification/useGetTeacherList";
import { useGetLightParents } from "../../../../hooks/school/notification/useGetParentList";

const SubSchoolSelector = ({
  selectedIds,
  onChange,
  disabled = false,
  onRoleChange,
  allowedTabs,
}) => {
  const { currentUser } = useTenantAuth();

  const { loading, getStudents } = useGetLightStudents();
  const { loading: teacherLoading, getTeachers } = useGetLightTeachers();
  const { loading: parentLoading, getParents } = useGetLightParents();

  const [sendTo, setSendTo] = useState("all");

  // Selection states for specific students
  const [specificStudentRoles, setSpecificStudentRoles] = useState({
    student: true,
    parent: false,
  });

  /* ================= PAGINATION STATES ================= */
  const [studentPage, setStudentPage] = useState(1);
  const [teacherPage, setTeacherPage] = useState(1);

  const [studentMeta, setStudentMeta] = useState(null);
  const [teacherMeta, setTeacherMeta] = useState(null);

  const [allStudents, setAllStudents] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);

  const [studentEnd, setStudentEnd] = useState(false);
  const [teacherEnd, setTeacherEnd] = useState(false);

  const [loadingMoreStudents, setLoadingMoreStudents] = useState(false);
  const [loadingMoreTeachers, setLoadingMoreTeachers] = useState(false);

  const [studentSearch, setStudentSearch] = useState("");
  const [teacherSearch, setTeacherSearch] = useState("");

  const [parentPage, setParentPage] = useState(1);
  const [parentMeta, setParentMeta] = useState(null);
  const [allParents, setAllParents] = useState([]);

  const [parentEnd, setParentEnd] = useState(false);
  const [loadingMoreParents, setLoadingMoreParents] = useState(false);

  const [parentSearch, setParentSearch] = useState("");

  const buildStudentFilters = (search) => {
    const filters = { status: 1 };
    if (search) filters.search = search;
    return filters;
  };

  const buildTeacherFilters = (search) => {
    if (!search) return {};

    return { search };
  };

  const buildParentFilters = (search) => {
    const filters = { status: 1 };
    if (search) filters.search = search;
    return filters;
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    loadStudents(1, studentSearch);
  }, [studentSearch]);

  useEffect(() => {
    loadTeachers(1, teacherSearch);
  }, [teacherSearch]);

  useEffect(() => {
    loadParents(1, parentSearch);
  }, [parentSearch]);

  /* ================= LOAD FUNCTIONS ================= */
  const loadStudents = async (page, search) => {
    if (page > 1) setLoadingMoreStudents(true);

    const res = await getStudents({
      studentId: currentUser?.student_id,
      page,
      filters: buildStudentFilters(search),
    });

    if (res?.success) {
      setStudentMeta(res.meta);

      setAllStudents((prev) =>
        page === 1 ? res.data : [...prev, ...res.data],
      );

      setStudentEnd(res.meta.current_page >= res.meta.last_page);
      setStudentPage(page);
    }

    setLoadingMoreStudents(false);
  };

  const loadTeachers = async (page, search) => {
    if (page > 1) setLoadingMoreTeachers(true);

    const res = await getTeachers({
      teacherId: currentUser?.teacher_id,
      page, // append for infinite scroll
      filters: buildTeacherFilters(search),
    });

    if (res?.success) {
      setTeacherMeta(res.meta);

      setAllTeachers((prev) =>
        page === 1 ? res.data : [...prev, ...res.data],
      );

      setTeacherEnd(res.meta.current_page >= res.meta.last_page);
      setTeacherPage(page);
    }

    setLoadingMoreTeachers(false);
  };

  const loadParents = async (page, search) => {
    if (page > 1) setLoadingMoreParents(true);

    const res = await getParents({
      page,
      filters: buildParentFilters(search),
    });

    if (res?.success) {
      setParentMeta(res.meta);

      setAllParents((prev) => (page === 1 ? res.data : [...prev, ...res.data]));

      setParentEnd(res.meta.current_page >= res.meta.last_page);
      setParentPage(page);
    }

    setLoadingMoreParents(false);
  };

  /* ================= ROLE AUTO UPDATE ================= */
  const updateRoles = (newSelectedIds, stuRoles = specificStudentRoles) => {
    const roles = [];

    if (newSelectedIds.send_all_students) {
      roles.push("student");
    } else if (newSelectedIds.student_ids?.length > 0) {
      if (stuRoles.student) roles.push("student");
      if (stuRoles.parent) roles.push("parent");
    }

    if (
      newSelectedIds.send_all_teachers ||
      newSelectedIds.user_ids?.length > 0
    ) {
      roles.push("teacher");
    }

    if (
      newSelectedIds.send_all_parents ||
      newSelectedIds.parent_ids?.length > 0
    ) {
      roles.push("parent");
    }

    const uniqueRoles = [...new Set(roles)];
    onRoleChange?.(uniqueRoles);
  };

  const handleSpecificStudentRoleChange = (role) => {
    const newRoles = {
      ...specificStudentRoles,
      [role]: !specificStudentRoles[role],
    };

    // Ensure at least one is selected
    if (!newRoles.student && !newRoles.parent) {
      return;
    }

    setSpecificStudentRoles(newRoles);
    updateRoles(selectedIds, newRoles);
  };

  const handleStudentSearch = (value) => {
    setStudentSearch(value);
    setStudentPage(1);
    setStudentEnd(false);
    // setAllStudents([]);
  };

  const handleTeacherSearch = (value) => {
    setTeacherSearch(value);
    setTeacherPage(1);
    setTeacherEnd(false);
    // setAllTeachers([]);
  };

  /* ================= SCROLL HANDLERS ================= */
  const handleStudentScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 20;

    if (
      bottom &&
      studentMeta &&
      studentMeta.current_page < studentMeta.last_page &&
      !loading &&
      !loadingMoreStudents
    ) {
      loadStudents(studentPage + 1, studentSearch);
    }
  };

  const handleTeacherScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 20;

    if (
      bottom &&
      teacherMeta &&
      teacherMeta.current_page < teacherMeta.last_page &&
      !teacherLoading &&
      !loadingMoreTeachers
    ) {
      loadTeachers(teacherPage + 1, teacherSearch);
    }
  };

  const handleParentScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 20;

    if (
      bottom &&
      parentMeta &&
      parentMeta.current_page < parentMeta.last_page &&
      !parentLoading &&
      !loadingMoreParents
    ) {
      loadParents(parentPage + 1, parentSearch);
    }
  };

  const handleSelectAllStudents = () => {
    if (sendTo === "all") {
      const newValue = !selectedIds.send_all_students;
      const newSelectedIds = {
        ...selectedIds,
        student_ids: [],
        send_all_students: newValue,
      };
      onChange(newSelectedIds);
      updateRoles(newSelectedIds);
    } else {
      const allIds = allStudents.map((s) => s.id);
      const updated =
        selectedIds.student_ids.length === allIds.length ? [] : allIds;
      const newSelectedIds = {
        ...selectedIds,
        student_ids: updated,
        send_all_students: false,
      };
      onChange(newSelectedIds);
      updateRoles(newSelectedIds);
    }
  };

  const handleSelectAllTeachers = () => {
    if (sendTo === "all") {
      const newValue = !selectedIds.send_all_teachers;
      const newSelectedIds = {
        ...selectedIds,
        user_ids: [],
        send_all_teachers: newValue,
      };
      onChange(newSelectedIds);
      updateRoles(newSelectedIds);
    } else {
      const allIds = allTeachers.map((t) => t.id);
      const updated =
        selectedIds.user_ids.length === allIds.length ? [] : allIds;
      const newSelectedIds = {
        ...selectedIds,
        user_ids: updated,
        send_all_teachers: false,
      };
      onChange(newSelectedIds);
      updateRoles(newSelectedIds);
    }
  };

  const handleSelectAllParents = () => {
    if (sendTo === "all") {
      const newValue = !selectedIds.send_all_parents;

      const newSelectedIds = {
        ...selectedIds,
        parent_ids: [],
        send_all_parents: newValue,
      };

      onChange(newSelectedIds);
      updateRoles(newSelectedIds);
    } else {
      const allIds = allParents.map((p) => p.id);

      const updated =
        selectedIds.parent_ids.length === allIds.length ? [] : allIds;

      const newSelectedIds = {
        ...selectedIds,
        parent_ids: updated,
        send_all_parents: false,
      };

      onChange(newSelectedIds);
      updateRoles(newSelectedIds);
    }
  };

  /* ================= INDIVIDUAL STUDENT ================= */
  const handleStudentChange = (id) => {
    const updated = selectedIds.student_ids.includes(id)
      ? selectedIds.student_ids.filter((sid) => sid !== id)
      : [...selectedIds.student_ids, id];

    const newSelectedIds = {
      ...selectedIds,
      student_ids: updated,
    };

    onChange(newSelectedIds);
    updateRoles(newSelectedIds);
  };

  /* ================= INDIVIDUAL TEACHER ================= */
  const handleTeacherChange = (id) => {
    const updated = selectedIds.user_ids.includes(id)
      ? selectedIds.user_ids.filter((uid) => uid !== id)
      : [...selectedIds.user_ids, id];

    const newSelectedIds = {
      ...selectedIds,
      user_ids: updated,
    };

    onChange(newSelectedIds);
    updateRoles(newSelectedIds);
  };

  const handleParentChange = (id) => {
    const updated = selectedIds.parent_ids.includes(id)
      ? selectedIds.parent_ids.filter((pid) => pid !== id)
      : [...selectedIds.parent_ids, id];

    const newSelectedIds = {
      ...selectedIds,
      parent_ids: updated,
    };

    onChange(newSelectedIds);
    updateRoles(newSelectedIds);
  };

  return (
    <div className="sub-school-selector">
      <label className="sub-school-selector-label">Send To *</label>

      {/* Toggle */}
      <div className="sub-school-selector-toggle">
        {allowedTabs.includes("all") && (
          <button
            type="button"
            className={`toggle-btn ${sendTo === "all" ? "active" : ""}`}
            onClick={() => setSendTo("all")}
          >
            All
          </button>
        )}

        {allowedTabs.includes("specificStudent") && (
          <button
            type="button"
            className={`toggle-btn ${sendTo === "specificStudent" ? "active" : ""
              }`}
            onClick={() => setSendTo("specificStudent")}
          >
            Specific Students
          </button>
        )}

        {allowedTabs.includes("specificTeacher") && (
          <button
            type="button"
            className={`toggle-btn ${sendTo === "specificTeacher" ? "active" : ""
              }`}
            onClick={() => setSendTo("specificTeacher")}
          >
            Specific Teachers
          </button>
        )}

        {allowedTabs.includes("specificParent") && (
          <button
            type="button"
            className={`toggle-btn ${sendTo === "specificParent" ? "active" : ""
              }`}
            onClick={() => setSendTo("specificParent")}
          >
            Specific Parents
          </button>
        )}
      </div>

      {/* ================= ALL MODE ================= */}
      {sendTo === "all" && (
        <div className="sub-school-selector-list">
          {/* 👇 Agar specificStudent allowed hai to All Students dikhao */}
          {allowedTabs.includes("specificStudent") && (
            <label className="sub-school-checkbox-item">
              <input
                type="checkbox"
                checked={!!selectedIds.send_all_students}
                onChange={handleSelectAllStudents}
                disabled={disabled}
              />
              <span>All Students ({studentMeta?.total})</span>
            </label>
          )}

          {/* 👇 Agar specificTeacher allowed hai to All Teachers dikhao */}
          {allowedTabs.includes("specificTeacher") && (
            <label className="sub-school-checkbox-item">
              <input
                type="checkbox"
                checked={!!selectedIds.send_all_teachers}
                onChange={handleSelectAllTeachers}
                disabled={disabled}
              />
              <span>All Teachers ({teacherMeta?.total})</span>
            </label>
          )}

          {allowedTabs.includes("allParent") && (
            <label className="sub-school-checkbox-item">
              <input
                type="checkbox"
                checked={!!selectedIds.send_all_parents}
                onChange={handleSelectAllParents}
                disabled={disabled}
              />
              <span>All Parents ({parentMeta?.total})</span>
            </label>
          )}
        </div>
      )}

      {/* ================= SPECIFIC STUDENTS ================= */}
      {sendTo === "specificStudent" && (
        <div
          className="sub-school-selector-list"
          style={{ maxHeight: "300px", overflowY: "auto" }}
          onScroll={handleStudentScroll}
        >
          <SearchableList
            items={allStudents}
            selectedIds={selectedIds.student_ids}
            onSelect={handleStudentChange}
            onSelectAll={(filteredItems) => {
              const filteredIds = filteredItems.map((i) => i.id);

              const allSelected = filteredIds.every((id) =>
                selectedIds.student_ids.includes(id),
              );

              const updated = allSelected
                ? selectedIds.student_ids.filter(
                  (id) => !filteredIds.includes(id),
                )
                : [...new Set([...selectedIds.student_ids, ...filteredIds])];

              const newSelectedIds = {
                ...selectedIds,
                student_ids: updated,
              };

              onChange(newSelectedIds);
              updateRoles(newSelectedIds);
            }}
            renderTopSelection={() => (
              <div className="d-flex px-3 pb-2 pt-3 gap-4 border-bottom mb-2">
                <label
                  className="d-flex align-items-center gap-2 m-0"
                  style={{ cursor: "pointer", fontSize: "14px" }}
                >
                  <input
                    type="checkbox"
                    checked={specificStudentRoles.student}
                    onChange={() => handleSpecificStudentRoleChange("student")}
                    className="form-check-input mt-0"
                  />
                  <span>Students</span>
                </label>
                <label
                  className="d-flex align-items-center gap-2 m-0"
                  style={{ cursor: "pointer", fontSize: "14px" }}
                >
                  <input
                    type="checkbox"
                    checked={specificStudentRoles.parent}
                    onChange={() => handleSpecificStudentRoleChange("parent")}
                    className="form-check-input mt-0"
                  />
                  <span>Parents</span>
                </label>
              </div>
            )}
            onSearch={handleStudentSearch} // 👈 IMPORTANT
            disabled={disabled}
            placeholder="Search students..."
            renderLabel={(student) => `${student?.name} (${student?.username})`}
          />

          {loadingMoreStudents && (
            <div className="scroll-loader">Loading more students...</div>
          )}

          {studentEnd && !loadingMoreStudents && (
            <div className="scroll-end">
              🎉 You've reached the end — no more students to show.
            </div>
          )}
        </div>
      )}

      {/* ================= SPECIFIC TEACHERS ================= */}
      {sendTo === "specificTeacher" && (
        <div
          className="sub-school-selector-list"
          style={{ maxHeight: "300px", overflowY: "auto" }}
          onScroll={handleTeacherScroll}
        >
          <SearchableList
            items={allTeachers}
            selectedIds={selectedIds.user_ids}
            onSelect={handleTeacherChange}
            onSelectAll={(filteredItems) => {
              const filteredIds = filteredItems.map((i) => i.id);

              const allSelected = filteredIds.every((id) =>
                selectedIds.user_ids.includes(id),
              );

              const updated = allSelected
                ? selectedIds.user_ids.filter((id) => !filteredIds.includes(id))
                : [...new Set([...selectedIds.user_ids, ...filteredIds])];

              const newSelectedIds = {
                ...selectedIds,
                user_ids: updated,
              };

              onChange(newSelectedIds);
              updateRoles(newSelectedIds);
            }}
            onSearch={handleTeacherSearch}
            disabled={disabled}
            placeholder="Search teachers by email..."
            renderLabel={(teacher) =>
              `${teacher?.firstName} ${teacher?.lastName} (${teacher?.email})`
            }
          />

          {loadingMoreTeachers && (
            <div className="scroll-loader">Loading more teachers...</div>
          )}

          {teacherEnd && !loadingMoreTeachers && (
            <div className="scroll-end">
              🎉 You've reached the end — no more teachers to show.
            </div>
          )}
        </div>
      )}

      {sendTo === "specificParent" && (
        <div
          className="sub-school-selector-list"
          style={{ maxHeight: "300px", overflowY: "auto" }}
          onScroll={handleParentScroll}
        >
          <SearchableList
            items={allParents}
            selectedIds={selectedIds.parent_ids}
            onSelect={handleParentChange}
            onSelectAll={(filteredItems) => {
              const filteredIds = filteredItems.map((i) => i.id);

              const allSelected = filteredIds.every((id) =>
                selectedIds.parent_ids.includes(id),
              );

              const updated = allSelected
                ? selectedIds.parent_ids.filter(
                  (id) => !filteredIds.includes(id),
                )
                : [...new Set([...selectedIds.parent_ids, ...filteredIds])];

              const newSelectedIds = {
                ...selectedIds,
                parent_ids: updated,
              };

              onChange(newSelectedIds);
              updateRoles(newSelectedIds);
            }}
            onSearch={(value) => {
              setParentSearch(value);
              setParentPage(1);
              setParentEnd(false);
            }}
            disabled={disabled}
            placeholder="Search parents..."
            renderLabel={(parent) => `${parent?.name} (${parent?.username})`}
          />

          {loadingMoreParents && (
            <div className="scroll-loader">Loading more parents...</div>
          )}

          {parentEnd && !loadingMoreParents && (
            <div className="scroll-end">
              🎉 You've reached the end — no more parents to show.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubSchoolSelector;
